import { useCallback, useEffect, useMemo, useState } from 'react';
import { Cascader } from 'antd';
import { CascaderProps, DefaultOptionType } from 'antd/es/cascader';

import { DepartmentDto } from '@app/integ-apigee-interfaces';
import { Theme } from '@app/web-components';

import { constructDepartmentTree } from '../../data/department-tree';
import { useFindAllDepartments } from '../../data/departments';

const TOP_LEVEL_DEPARTMENT = {
  departmentCd: '22790',
  departmentNm: 'Executive Office',
  parentDepartmentCd: '10000',
  parentDepartmentNm: 'Intel Board of Directors',
};

const DEFAULT_WIDTH = 400;

export type DepartmentCascaderProps = Omit<CascaderProps, 'multiple' | 'options' | 'onSearch' | 'onChange'> & {
  parentDepartmentCd?: string;
  value?: string;
  onChange?: (value: string) => void;
};

export const DepartmentCascader = ({ value, onChange, style, ...props }: DepartmentCascaderProps) => {
  const { data, isPending, isFetching } = useFindAllDepartments();
  const [selectedValue, setSelectedValue] = useState<string | undefined>(value);
  const [pathValue, setPathValue] = useState<string[]>([]);
  const { token } = Theme.useToken();

  const departmentTree = useMemo(() => {
    if (data && data.length > 0) {
      return constructDepartmentTree(data ?? [], TOP_LEVEL_DEPARTMENT.departmentCd);
    }
  }, [data]);

  const getAncestorPath = useCallback(
    (value: string): string[] | null => {
      const findParent = (dept: DepartmentDto): string[] => {
        const parent = data?.find((item) => item.departmentCd === dept.parentDepartmentCd);
        return parent && parent.departmentCd !== TOP_LEVEL_DEPARTMENT.parentDepartmentCd
          ? [dept.departmentCd, ...findParent(parent)]
          : [];
      };

      const currSelectedNode = data?.find((i) => i.departmentCd === value);
      return currSelectedNode ? findParent(currSelectedNode) : [];
    },
    [data],
  );

  useEffect(() => {
    if (selectedValue) {
      const ancestorPath = getAncestorPath(selectedValue);
      if (ancestorPath) {
        setPathValue(ancestorPath.reverse());
      }
    }
  }, [selectedValue, getAncestorPath]);

  const filter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some((option) => (option.label as string).toLowerCase().includes(inputValue.toLowerCase()));

  const handleChange = (value: (string | number)[], selectedOptions: DefaultOptionType[]) => {
    setSelectedValue(selectedOptions?.slice(-1)[0].value as string);
    onChange && onChange(selectedOptions?.slice(-1)[0].value as string);
  };

  const displayDropdown = (menus: React.ReactNode) => (
    <div>
      {menus}
      <div style={{ padding: 8, color: token.colorInfo, fontStyle: 'italic' }}>
        Tip: Double-click or click outside popup to select highlighted node in navigate mode.
      </div>
    </div>
  );

  return (
    <Cascader
      {...props}
      value={pathValue}
      style={{ width: DEFAULT_WIDTH, ...style }}
      loading={isPending || isFetching}
      options={departmentTree}
      showSearch={{ filter, limit: 100 }}
      displayRender={(label) => label?.slice(-1)[0]} // just display the final selection, without the whole path
      dropdownRender={displayDropdown} // add footer
      changeOnSelect
      onChange={handleChange}
    />
  );
};
